/**
 * Centralized Axios Instance with Request/Response Interceptors
 * 
 * This file creates a shared axios instance that all service files will use.
 * It automatically adds cache-busting to all GET requests to prevent browser caching issues.
 * 
 * @module services/axiosInstance
 */

import axios from 'axios'

/**
 * Create a custom axios instance
 * This is identical to the default axios, but allows us to add interceptors
 * that will apply to ALL requests made through this instance
 */
const axiosInstance = axios.create()


axiosInstance.interceptors.request.use(
  (config) => {
    /**
     * This function runs before EVERY request
     * For GET requests, it adds cache-busting mechanisms to prevent stale data
     * 
     * @param {Object} config - The axios request configuration
     * @returns {Object} Modified config that will be used for the request
     */

    // Only apply cache-busting to GET requests
    const isGet = config.method === 'get' || config.method === 'GET'

    // Only touch URLs that look like cometchat
    // This protects Google, Stripe, etc.
    const isInternal =
      config.url &&
      (config.url.includes('cometchat') || config.url.includes('cc-cluster'))

    // Check if this endpoint should be excluded from cache-busting
    // /vcb/builders returns 400 when cache-busting params are present
    const isExcludedEndpoint = config.url && config.url.includes('/vcb/builders')

    if (isGet && isInternal && !isExcludedEndpoint) {
      /**
       * Add cache-busting mechanisms:
       */

      // METHOD 1: Add timestamp to query params
      // This changes the URL, forcing browsers to treat it as a new request
      config.params = {
        ...config.params, // Preserve existing params (important!)
        _: Date.now(), // Add timestamp cache-buster
      }

      // // METHOD 2: Add cache-control headers
      // // This tells browsers and proxies not to cache the response
      config.headers = {
        ...config.headers, // Preserve existing headers
        'Cache-Control': 'no-cache, no-store, must-revalidate',
      }
    }

    return config
  },
  (error) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  },
)

// RESPONSE INTERCEPTOR - Runs AFTER every response is received
axiosInstance.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    return Promise.reject(error)
  },
)

export default axiosInstance
